import { yupResolver } from '@hookform/resolvers/yup';
import { CountryCode } from 'libphonenumber-js';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { Control, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAddEditSupplier } from '@/modules/settings/company/apiHooks';
import { useCompanySettingsUtils } from '@/modules/settings/company/components/logic';
import { ICompanySettings } from '@/modules/settings/company/types';
import {
  Button,
  InputField,
  PhoneInputWithCountry,
  Typography,
} from '@/shared/components/atoms';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const AddEditSupplier: React.FC<{
  id?: number;
  defaultValues?: ICompanySettings.ISupplierInfo;
}> = ({ id, defaultValues }) => {
  const { t } = useTranslation();

  const [asset] = useStore((store) => store.asset);

  const { supplierValidationSchema } = useCompanySettingsUtils();

  const { closeModal } = useModal();

  const getDefaultValue = (value?: string | null) =>
    value?.length ? value : undefined;

  const formMethods = useForm<ICompanySettings.ISupplierInfo>({
    resolver: yupResolver(supplierValidationSchema),
    defaultValues: defaultValues
      ? {
          name: defaultValues.name,
          phoneNumber: getDefaultValue(defaultValues.phoneNumber),
          communicationOfficer: getDefaultValue(
            defaultValues.communicationOfficer
          ),
          contactOfficerNumber: getDefaultValue(
            defaultValues.contactOfficerNumber
          ),
        }
      : undefined,
    mode: 'onSubmit',
  });
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = formMethods;

  const { mutate, isLoading } = useAddEditSupplier();

  const onSubmit: SubmitHandler<ICompanySettings.ISupplierInfo> = (data) => {
    mutate(
      { data, id },
      {
        onSuccess: () => {
          closeModal();
          toast.success(
            t(`${defaultValues ? 'edit' : 'add'}-supplier-success`)
          );
        },
      }
    );
  };
  return (
    <FormProvider {...formMethods}>
      <form
        className='m-auto flex h-full flex-col justify-between gap-16 pt-24 tablet:max-w-[75%] desktop:max-w-[50%]'
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          <Typography weight='semiBold' variant='h1' className='my-40'>
            {t(defaultValues ? 'edit-supplier' : 'add-supplier')}
          </Typography>
          <div className='flex flex-col gap-16'>
            <InputField
              borderColor='border-neutralVariant-90'
              width='w-full'
              py='py-1'
              label={t('supplier-name')}
              placeholder={t('supplier-name')}
              error={!!errors.name}
              errorMessage={errors.name?.message}
              disabled={isLoading}
              {...register('name')}
            />
            <div>
              <PhoneInputWithCountry
                disabled={isLoading}
                borderColor='border-neutralVariant-90 rounded-lg border-xs'
                width='w-full'
                label={`${t('contact-number')} (${t('optional')})`}
                placeholder={t('contact-number')}
                name='phoneNumber'
                id='phoneNumber'
                error={!!errors.phoneNumber}
                errorMessage={errors.phoneNumber?.message}
                defaultCountry={asset?.country as CountryCode}
                //  INFO: https://github.com/react-hook-form/react-hook-form/issues/4965
                control={control as unknown as Control<FieldValues>}
                required={false}
              />
            </div>
            <InputField
              borderColor='border-neutralVariant-90'
              width='w-full'
              py='py-1'
              label={t('communication-officer')}
              placeholder={t('communication-officer')}
              error={!!errors.communicationOfficer}
              errorMessage={errors.communicationOfficer?.message}
              disabled={isLoading}
              required={false}
              {...register('communicationOfficer')}
            />
            <div>
              <PhoneInputWithCountry
                borderColor='border-neutralVariant-90 rounded-lg border-xs'
                width='w-full'
                label={`${t('contact-officer-number')} (${t('optional')})`}
                placeholder={t('contact-officer-number')}
                id='contactOfficerNumber'
                name='contactOfficerNumber'
                error={!!errors.contactOfficerNumber}
                errorMessage={errors.contactOfficerNumber?.message}
                defaultCountry={asset?.country as CountryCode}
                //  INFO: https://github.com/react-hook-form/react-hook-form/issues/4965
                control={control as unknown as Control<FieldValues>}
                disabled={isLoading}
                required={false}
              />
            </div>
          </div>
        </div>
        <Button className='h-40' size='full' loading={isLoading} type='submit'>
          {t(defaultValues ? 'save-edits' : 'confirm')}
        </Button>
      </form>
    </FormProvider>
  );
};

export default AddEditSupplier;
